import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { LoginRequest } from '@offconon/core-api';

import * as DomainUserActions from './store/domain-user.actions';
import {
  selectDomainUser,
  selectDomainUserToken,
  selectDomainUserError,
  selectDomainUserLoading,
} from './store/domain-user.selectors';

/**
 * Signal-based facade for the Domain User NgRx store.
 *
 * Sections cannot inject NgRx `Store` directly (see creating-a-new-section guide).
 * This facade wraps the store with signals so sections can safely consume user state.
 *
 * API surface intentionally matches the old `LoginSectionStore` for easy migration.
 */
@Injectable({ providedIn: 'root' })
export class DomainUserFacade {
  private readonly store = inject(Store);

  /** Current login response data (null when not logged in). */
  readonly data = toSignal(this.store.select(selectDomainUser), { initialValue: null });

  /** Whether a login request is in progress. */
  readonly loading = toSignal(this.store.select(selectDomainUserLoading), { initialValue: false });

  /** Last error from a login attempt. */
  readonly error = toSignal(this.store.select(selectDomainUserError), { initialValue: null });

  /** Whether the user is currently logged in. */
  readonly isLoggedIn = computed(() => !!this.data());

  /** Current user token (JWT for logged in, Guest Token for guest). */
  readonly token = toSignal(this.store.select(selectDomainUserToken), { initialValue: null });

  /**
   * Trigger a login request.
   */
  login(loginRequest: LoginRequest): void {
    this.store.dispatch(DomainUserActions.domainUserLogin({ loginRequest }));
  }

  /**
   * Clear user state (logout).
   */
  clear(): void {
    this.store.dispatch(DomainUserActions.domainUserLogout());
  }
}
